import type { CheckedState } from '@radix-ui/react-checkbox'
import { DollarSign, Calendar } from 'lucide-react'
import type { InputHTMLAttributes } from 'react'
import { useState } from 'react'
import type { FieldValues, Path, Control, PathValue } from 'react-hook-form'

import { Eye } from 'src/shared/components/icons/eye'
import { EyeOff } from 'src/shared/components/icons/eye-off'
import { Checkbox } from 'src/shared/components/ui/checkbox'
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormErrorMessages,
} from 'src/shared/components/ui/forms/form'
import { FloatingLabelInput } from 'src/shared/components/ui/inputs/floating-label-input'
import { Input } from 'src/shared/components/ui/inputs/input'
import { Switch } from 'src/shared/components/ui/miscellaneous/switch'
import { cn } from 'src/shared/utils/cn'
import { formatCurrency, parseCurrencyToNumber } from 'src/shared/utils/format-currency'
import { formatPhoneNumber } from 'src/shared/utils/format-phone-number'

interface InputFormFieldProps<T extends FieldValues> extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  name: Path<T>
  label: string
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'currency' | 'date'
  description?: string
  floatingLabel?: boolean
  className?: string
  fieldClassName?: string
  fieldContainerClassName?: string
  required?: boolean
  control?: Control<T>
  LeftIcon?: React.ElementType
  RightIcon?: React.ElementType
  min?: number
  max?: number
  maxLength?: number
  allowDecimals?: boolean
  decimalPlaces?: number
  placeholder?: string
  checkbox?: {
    label: string
    defaultChecked?: boolean
  }
  switch?: {
    defaultChecked?: boolean
  }
  labelClassName?: string
  errorClassName?: string
  /** Override the default class name added when using the LeftIcon and RightIcon */
  fieldClassNameOverride?: string
  floatingLabelClassName?: string
  autoComplete?: string
  /** Note: If your error messages are not showing up, set this to true */
  useNewErrorMessageComponent?: boolean
  rightIconClassName?: string
  autoFocus?: boolean
}

export function InputFormField<T extends FieldValues>({
  name,
  label,
  type = 'text',
  description,
  floatingLabel = true,
  className,
  fieldClassName,
  disabled,
  required,
  control,
  LeftIcon,
  RightIcon,
  min = 0,
  max = 1000000000,
  maxLength,
  allowDecimals = true,
  decimalPlaces = 2,
  placeholder,
  checkbox,
  labelClassName,
  switch: switchProp,
  errorClassName,
  fieldClassNameOverride,
  floatingLabelClassName,
  fieldContainerClassName,
  autoComplete,
  useNewErrorMessageComponent = false,
  rightIconClassName,
  autoFocus,
}: InputFormFieldProps<T>) {
  const InputComponent = floatingLabel ? FloatingLabelInput : Input
  const [isDisabled, setIsDisabled] = useState(checkbox?.defaultChecked ?? false)
  const [isVisible, setIsVisible] = useState(switchProp?.defaultChecked ?? true)
  const [showPassword, setShowPassword] = useState(false)

  if (required && !isDisabled) {
    label = `${label} *`
  }

  const validateNumberInput = (value: string) => {
    let cleanValue = value.replace(/[^0-9.]/g, '')

    if (!allowDecimals) {
      cleanValue = cleanValue.replace(/\./g, '')
    } else if (cleanValue.includes('.')) {
      const [whole, decimal] = cleanValue.split('.')
      cleanValue = `${whole}.${(decimal || '').slice(0, decimalPlaces)}`
    }

    const numValue = parseFloat(cleanValue)

    if (!isNaN(numValue)) {
      if (min !== undefined && numValue < min) return min
      if (max !== undefined && numValue > max) return max
      return numValue
    }

    return ''
  }

  return (
    <FormField
      control={control}
      name={name}
      render={({ field: { onChange, ...field }, formState: { errors } }) => {
        const errorMessages = errors[name]?.message ? [errors[name].message as string] : []
        return (
          <FormItem className={cn('w-full', className)}>
            {(switchProp || !floatingLabel) && (
              <div className="flex items-center justify-start gap-4">
                {switchProp && (
                  <Switch
                    checked={isVisible}
                    onCheckedChange={(checked: CheckedState) => {
                      setIsVisible(!!checked)
                      onChange(checked ? undefined : field.value)
                    }}
                  />
                )}
                {!floatingLabel && <FormLabel className={labelClassName}>{label}</FormLabel>}
              </div>
            )}
            {isVisible && (
              <FormControl>
                <div
                  className={cn(
                    'relative',
                    (LeftIcon || type === 'currency' || type === 'date') && 'input-with-icon',
                    fieldContainerClassName,
                  )}
                >
                  {type === 'currency' ? (
                    <DollarSign className="absolute left-3 top-1/2 z-20 size-5 -translate-y-1/2 text-muted" />
                  ) : type === 'date' ? (
                    <Calendar className="absolute left-3 top-1/2 z-20 size-5 -translate-y-1/2 text-muted" />
                  ) : (
                    LeftIcon && <LeftIcon className="absolute left-3 top-1/2 z-20 size-5 -translate-y-1/2 text-muted" />
                  )}
                  <InputComponent
                    {...field}
                    autoComplete={autoComplete}
                    label={label}
                    id={field.name}
                    type={
                      type === 'currency' ? 'text' : type === 'password' ? (showPassword ? 'text' : 'password') : type
                    }
                    maxLength={maxLength}
                    disabled={disabled || isDisabled}
                    // Note: placeholder needs to be a space for safari, otherwise placeholder-shown pseudo-class is not triggered.
                    placeholder={!floatingLabel ? placeholder : ' '}
                    className={cn(
                      fieldClassName,
                      (LeftIcon || type === 'currency' || type === 'date') && 'pl-10',
                      (RightIcon || type === 'password') && 'pr-10',
                      type === 'date' && '[&::-webkit-calendar-picker-indicator]:hidden',
                      fieldClassNameOverride,
                    )}
                    {...(floatingLabel && { labelClassName: floatingLabelClassName })}
                    onChange={(e) => {
                      const value = e.target.value

                      if (type === 'date') {
                        onChange(value ? (new Date(value) as PathValue<T, Path<T>>) : undefined)
                      } else if (type === 'currency') {
                        if (!value || value === '.') {
                          onChange(undefined as PathValue<T, Path<T>>)
                          return
                        }
                        const numValue = parseCurrencyToNumber(value)
                        onChange(numValue as PathValue<T, Path<T>>)
                      } else if (type === 'number') {
                        const validatedValue = validateNumberInput(value)
                        onChange(validatedValue ? (validatedValue as PathValue<T, Path<T>>) : undefined)
                      } else if (type === 'tel') {
                        onChange(formatPhoneNumber(value))
                      } else if (type === 'email') {
                        onChange(value ? value : '')
                      } else {
                        onChange(maxLength ? value.slice(0, maxLength) : value)
                      }
                    }}
                    value={
                      type === 'date'
                        ? field.value && typeof field.value.toISOString === 'function'
                          ? field.value.toISOString().split('T')[0]
                          : ''
                        : type === 'currency'
                        ? formatCurrency(field.value as number)
                        : type === 'number'
                        ? (field.value ?? '').toString()
                        : field.value ?? ''
                    }
                    autoFocus={autoFocus}
                  />
                  {type === 'password' ? (
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={cn(
                        'absolute right-3 top-1/2 z-20 -translate-y-1/2 cursor-pointer text-muted hover:text-foreground',
                        rightIconClassName,
                      )}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={22} /> : <Eye size={22} className="mt-px" />}
                    </button>
                  ) : (
                    RightIcon && (
                      <RightIcon
                        className={cn(
                          'absolute right-3 top-1/2 size-5 -translate-y-1/2 text-muted',
                          floatingLabel && 'z-10',
                          rightIconClassName,
                        )}
                      />
                    )
                  )}
                </div>
              </FormControl>
            )}
            {checkbox && (
              <div className="mt-2">
                <Checkbox
                  checked={isDisabled}
                  onCheckedChange={(checked: CheckedState) => {
                    setIsDisabled(!!checked)
                    onChange(checked ? undefined : field.value)
                  }}
                  id={`${field.name}-checkbox`}
                  className="mr-2"
                />
                <label htmlFor={`${field.name}-checkbox`} className="text-sm text-muted">
                  {checkbox.label}
                </label>
              </div>
            )}
            {description && <FormDescription>{description}</FormDescription>}
            {useNewErrorMessageComponent ? (
              <FormErrorMessages messages={errorMessages} className={errorClassName} />
            ) : (
              <FormMessage className={errorClassName} />
            )}
          </FormItem>
        )
      }}
    />
  )
}
